// A singleton resource held by one lease at a time: the leases line up, and
// each one sets the item up when its turn comes and tears it down when it ends.

export class SingletonEvictedError extends Error {
  constructor() {
    super("The singleton was taken over by another acquisition");

    // Keep `instanceof` working when compiled to ES5.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// How to acquire the singleton when it is already held or waited for:
// - `wait` - line up after the current holder and the waiters;
// - `evict` - evict the current holder and the waiters first.
export type AcquireMode = "wait" | "evict";

export type SingletonHandle<Item> = {
  // Rejects with `SingletonEvictedError` if evicted before the turn.
  item: Promise<Item>;
  // Resolves when the lease is evicted by another acquisition.
  evicted: Promise<void>;
  // Ends the lease, letting the next one in line have the singleton.
  release: () => void;
};

const deferred = () => {
  let resolve: () => void = () => {};
  const promise = new Promise<void>((r) => {
    resolve = r;
  });
  return { promise, resolve };
};

export type DeinitFn = () => Promise<void>;

// Starts the init and returns the item along with the fn to deinit it; the
// deinit fn is called once the item settles, even if the init has failed.
export type InitFn<Item> = () => [item: Promise<Item>, deinit: DeinitFn];

export type Singleton<Item> = {
  acquire: (init: InitFn<Item>, mode: AcquireMode) => SingletonHandle<Item>;
  // Acquires only if no lease is held, waited for or still ending; returns
  // `null` otherwise.
  tryAcquire: (init: InitFn<Item>) => SingletonHandle<Item> | null;
};

type LogFn = (...args: unknown[]) => void;

// Returns the fn to create the log of a new lease, which tells the leases
// apart by their number; nothing is logged when the singleton has no name.
const createLeaseLogs = (name: string | undefined): (() => LogFn) => {
  let nextLeaseId = 0;

  return () => {
    const leaseId = nextLeaseId++;

    return (...args) => {
      if (name === undefined) return;
      console.log(`[singleton ${name}] lease ${leaseId}:`, ...args);
    };
  };
};

// The leases lining up, being evicted and ending are logged to the console
// when the singleton is given a name.
const createSingleton = <Item>(name?: string): Singleton<Item> => {
  const createLog = createLeaseLogs(name);

  // The leases that have not ended yet: the holder and the waiters.
  const leases = new Set<{ evict: () => void }>();

  // Resolves when the last lease in line ends.
  let tail: Promise<void> = Promise.resolve();

  // Line up a new lease at the end of the line.
  const addLease = (init: InitFn<Item>): SingletonHandle<Item> => {
    const log = createLog();
    log(`lined up behind ${leases.size} lease(s)`);

    const evicted = deferred();
    const endRequested = deferred();
    let isEvicted = false;
    // Set once the init has started.
    let deinit: DeinitFn | undefined;

    const turn = tail;

    const item = (async () => {
      await turn;

      if (isEvicted) throw new SingletonEvictedError();

      // A throw here means the init has not started: nothing to deinit.
      const [initItem, initDeinit] = init();
      deinit = initDeinit;
      return initItem;
    })();

    const ended = (async () => {
      // Wait for the turn and the init to settle.
      await item.then(
        () => {},
        () => {},
      );

      await endRequested.promise;

      // Even a failed init may leave the item holding resources.
      if (deinit) await deinit();
    })();

    tail = ended;

    const lease = {
      evict: () => {
        if (isEvicted) return;
        log("evicted");
        isEvicted = true;
        evicted.resolve();
        endRequested.resolve();
      },
    };
    leases.add(lease);
    ended.then(() => {
      leases.delete(lease);
      log(`ended, ${leases.size} lease(s) left`);
    });

    return {
      item,
      evicted: evicted.promise,
      release: endRequested.resolve,
    };
  };

  const acquire = (
    init: InitFn<Item>,
    mode: AcquireMode,
  ): SingletonHandle<Item> => {
    if (mode === "evict") {
      leases.forEach((lease) => lease.evict());
    }

    return addLease(init);
  };

  const tryAcquire = (init: InitFn<Item>): SingletonHandle<Item> | null => {
    if (leases.size > 0) return null;

    return addLease(init);
  };

  return { acquire, tryAcquire };
};

export default createSingleton;
